/**
 * Cliente del taller.
 *
 * Los estados y sus transiciones vienen del **servidor**: `available_transitions`
 * ya llega filtrado por permisos y con la etiqueta en el idioma del mostrador.
 * El frontend no repite la máquina de estados —si lo hiciera, habría dos
 * versiones de la misma regla y una estaría mal—, sólo la pinta.
 */

import { apiRequest } from "./api";
import type { components } from "./api-types";

type Schemas = components["schemas"];

export type OrderStatus = Schemas["OrderPublic"]["status"];
export type OrderPriority = Schemas["OrderPublic"]["priority"];
export type OrderPublic = Schemas["OrderPublic"];
export type OrderDetailPublic = Schemas["OrderDetailPublic"];
export type CustomerPublic = Schemas["CustomerPublic"];
export type EstimatePublic = Schemas["EstimatePublic"];
export type EstimateLinePublic = Schemas["EstimateLinePublic"];
export type ChecklistItemPublic = Schemas["ChecklistItemPublic"];
export type TemplatePublic = Schemas["TemplatePublic"];
export type TransitionOption = Schemas["TransitionOption"];
export type ChargePublic = Schemas["ChargePublic"];
export type ChargeRequest = Schemas["ChargeRequest"];
export type OrderCreate = Schemas["OrderCreate"];
export type EstimateSave = Schemas["EstimateSave"];
export type EstimateLineInput = Schemas["EstimateLineInput"];
export type NotificationPublic = Schemas["NotificationPublic"];
export type BoardPublic = Schemas["BoardPublic"];

/** Los estados en el orden en que avanza el trabajo. */
export const STATUS_ORDER: OrderStatus[] = [
  "recibido",
  "diagnostico",
  "esperando_repuesto",
  "presupuestado",
  "aprobado",
  "en_reparacion",
  "listo",
  "entregado",
  "rechazado",
  "cancelado",
];

/** El nombre del estado como lo dice un taller, no como lo dice la base. */
export const STATUS_LABEL: Record<string, string> = {
  recibido: "Recibido",
  diagnostico: "En diagnóstico",
  esperando_repuesto: "Esperando repuesto",
  presupuestado: "Presupuestado",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
  en_reparacion: "En reparación",
  listo: "Listo para entregar",
  entregado: "Entregado",
  cancelado: "Cancelado",
};

export const STATUS_TONE: Record<string, "ok" | "warning" | "danger" | "info" | "neutral"> = {
  recibido: "neutral",
  diagnostico: "info",
  esperando_repuesto: "warning",
  presupuestado: "info",
  aprobado: "info",
  rechazado: "warning",
  en_reparacion: "info",
  listo: "ok",
  entregado: "ok",
  cancelado: "neutral",
};

const BASE = "/api/v1/workshop";

export function board(): Promise<BoardPublic> {
  return apiRequest<BoardPublic>(`${BASE}/board`);
}

export function listOrders(params: {
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: OrderDetailPublic[]; total: number }> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  if (params.status) query.set("status", params.status);
  if (params.q) query.set("q", params.q);
  return apiRequest(`${BASE}/orders?${query.toString()}`);
}

export function getOrder(orderId: string): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders/${orderId}`);
}

export function createOrder(payload: OrderCreate): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders`, { method: "POST", body: payload });
}

export function transitionOrder(
  orderId: string,
  toStatus: string,
  notes?: string,
): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders/${orderId}/transition`, {
    method: "POST",
    body: { to_status: toStatus, notes: notes ?? null },
  });
}

export function saveEstimate(
  orderId: string,
  lines: EstimateLineInput[],
  notes?: string,
): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders/${orderId}/estimate`, {
    method: "PUT",
    body: { lines, notes: notes ?? null },
  });
}

export function sendEstimate(orderId: string): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders/${orderId}/estimate/send`, {
    method: "POST",
  });
}

export function decideEstimate(
  orderId: string,
  approved: boolean,
  note?: string,
): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders/${orderId}/estimate/decision`, {
    method: "POST",
    body: { approved, note: note ?? null },
  });
}

export function updateChecklistItem(
  orderId: string,
  itemId: string,
  done: boolean,
  notes?: string,
): Promise<OrderDetailPublic> {
  return apiRequest<OrderDetailPublic>(`${BASE}/orders/${orderId}/checklist/${itemId}`, {
    method: "PATCH",
    body: { done, notes: notes ?? null },
  });
}

export function notifyCustomer(
  orderId: string,
  channel: "whatsapp" | "email" | "sms",
  target?: string,
  message?: string,
): Promise<NotificationPublic> {
  return apiRequest<NotificationPublic>(`${BASE}/orders/${orderId}/notify`, {
    method: "POST",
    body: { channel, target: target ?? null, message: message ?? null },
  });
}

export function chargeOrder(
  orderId: string,
  payload: ChargeRequest,
  idempotencyKey: string,
): Promise<ChargePublic> {
  return apiRequest<ChargePublic>(`${BASE}/orders/${orderId}/charge`, {
    method: "POST",
    body: payload,
    idempotencyKey,
  });
}

export function listTemplates(deviceType?: string): Promise<TemplatePublic[]> {
  const query = deviceType ? `?device_type=${encodeURIComponent(deviceType)}` : "";
  return apiRequest<TemplatePublic[]>(`${BASE}/checklist-templates${query}`);
}

export function createTemplate(payload: {
  device_type: string;
  label: string;
  position?: number;
  required?: boolean;
}): Promise<TemplatePublic> {
  return apiRequest<TemplatePublic>(`${BASE}/checklist-templates`, {
    method: "POST",
    body: payload,
  });
}

export function listCustomers(term?: string): Promise<CustomerPublic[]> {
  const query = term ? `?q=${encodeURIComponent(term)}` : "";
  return apiRequest<CustomerPublic[]>(`${BASE}/customers${query}`);
}
